/**
 * Five polarity-preserving buckets for a standalone rating.
 *
 * Rows stay on the 3-band Bottom-2 / Neutral / Top-2 helpers (`divergingBands`,
 * `divergingRowFromDistribution`). A standalone rating always renders five visual
 * bands — never one-per-point on a 7- or 11-point scale, and never the row grouping.
 *
 * Grouping (low → high):
 *   Exactly 5 integer steps (the common 1–5): one band per scale point.
 *   Any other span: band = round((v − min) / (max − min) × 4), clamped 0–4.
 *     1–7  → [1] | [2, 3] | [4] | [5, 6] | [7]
 *     0–10 → [0, 1] | [2, 3] | [4, 5, 6] | [7, 8] | [9, 10]
 *
 * Palette poles are the semantic chart tokens, not the deep brand green:
 *   --chart-positive = hsl(154 60% 45%) = #2eb87c
 *   --chart-negative = hsl(0 65% 55%)   = #d74242
 * Deutan ΔE 10.0 light / 12.3 dark (floor 8). `--chart-diverging-positive`
 * (hsl(145 60% 30%), the deep brand green) against the same red measures ΔE 3.9
 * and fails — do not use it as the favourable pole.
 */

import type { RatingPolarity } from '@digital-twins/shared-types'

import { roundHalfAwayFromZero } from '../../../foundations/producer-rounding'

export const FIVE_BAND_COUNT = 5

/**
 * --chart-negative → mid → --chart-positive (and intermediates via color-mix
 * against --chart-neutral). Never `--slide-chart-1` or `--chart-diverging-positive`.
 */
export const FIVE_BAND_COLORS: Array<string> = [
  'hsl(var(--chart-negative))',
  'color-mix(in srgb, hsl(var(--chart-negative)) 60%, hsl(var(--chart-neutral)))',
  'hsl(var(--chart-neutral))',
  'color-mix(in srgb, hsl(var(--chart-positive)) 60%, hsl(var(--chart-neutral)))',
  'hsl(var(--chart-positive))',
]

/**
 * Scale-ordered palette (low → high). On a lower-is-better scale the
 * favourable (low) end takes `--chart-positive` so a reversed rating does not
 * paint "extremely concerned" bright green; on a middle-is-best scale the
 * middle takes it and both ends step toward `--chart-negative`.
 */
export function fiveBandPalette(polarity: RatingPolarity = 'higher_is_better'): Array<string> {
  if (polarity === 'higher_is_better') {
    return FIVE_BAND_COLORS
  }
  if (polarity === 'middle_is_best') {
    return [
      FIVE_BAND_COLORS[0],
      FIVE_BAND_COLORS[1],
      FIVE_BAND_COLORS[4],
      FIVE_BAND_COLORS[1],
      FIVE_BAND_COLORS[0],
    ]
  }
  return [
    FIVE_BAND_COLORS[4],
    FIVE_BAND_COLORS[3],
    FIVE_BAND_COLORS[2],
    FIVE_BAND_COLORS[1],
    FIVE_BAND_COLORS[0],
  ]
}

export interface FiveBandPoint {
  rawValue: unknown
  value: number
}

export interface FiveBand {
  index: number
  scaleValues: Array<number>
  count: number
  total: number
  percent: number
  leftPct: number
  widthPct: number
  label: string
  color: string
}

export function fiveBandIndex(value: number, scaleMin: number, scaleMax: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(scaleMin) || !Number.isFinite(scaleMax)) {
    return -1
  }
  const span = scaleMax - scaleMin
  if (!(span > 0)) {
    return -1
  }
  const steps = Math.round(span) + 1
  if (steps === FIVE_BAND_COUNT) {
    return Math.max(0, Math.min(FIVE_BAND_COUNT - 1, Math.round(value - scaleMin)))
  }
  const t = (value - scaleMin) / span
  return Math.max(0, Math.min(FIVE_BAND_COUNT - 1, Math.round(t * (FIVE_BAND_COUNT - 1))))
}

export function formatFiveBandLabel(count: number, total: number): string {
  if (count <= 0 || total <= 0) {
    return ''
  }
  // Producer rounding rule (half away from zero).
  const pct = roundHalfAwayFromZero((100 * count) / total)
  if (pct === 0) {
    return String(count)
  }
  return `${pct}%`
}

function formatScaleRange(values: Array<number>): string {
  if (values.length === 0) {
    return ''
  }
  if (values.length === 1) {
    return String(values[0])
  }
  return `${values[0]}–${values[values.length - 1]}`
}

/** Single full-width band so a min===max scale still draws a bar. */
function degenerateFiveBand(
  points: Array<FiveBandPoint>,
  scaleValue: number,
  polarity: RatingPolarity,
): Array<FiveBand> | null {
  let total = 0
  for (const point of points) {
    if (point.rawValue !== null && typeof point.rawValue === 'object') {
      continue
    }
    const raw = Number(point.rawValue)
    const count = Number(point.value) || 0
    if (!Number.isFinite(raw) || count <= 0) {
      continue
    }
    total += count
  }
  if (total <= 0) {
    return null
  }
  const palette = fiveBandPalette(polarity)
  return [
    {
      index: 0,
      scaleValues: [scaleValue],
      count: total,
      total,
      percent: 100,
      leftPct: 0,
      widthPct: 100,
      label: formatScaleRange([scaleValue]),
      color: palette[2],
    },
  ]
}

function collectScaleValues(scaleMin: number, scaleMax: number): Array<Array<number>> {
  const buckets: Array<Array<number>> = Array.from({ length: FIVE_BAND_COUNT }, () => [])
  const start = Math.round(scaleMin)
  const end = Math.round(scaleMax)
  for (let value = start; value <= end; value += 1) {
    const index = fiveBandIndex(value, scaleMin, scaleMax)
    if (index >= 0) {
      buckets[index].push(value)
    }
  }
  return buckets
}

/** Build five left-anchored bands from `aggregateSimple` output (or any `{ rawValue, value }` counts). */
export function fiveBandFromDistribution(
  points: Array<FiveBandPoint>,
  scaleMin: number,
  scaleMax: number,
  polarity: RatingPolarity = 'higher_is_better',
): Array<FiveBand> | null {
  if (!Number.isFinite(scaleMin) || !Number.isFinite(scaleMax)) {
    return null
  }
  if (!(scaleMax > scaleMin)) {
    if (scaleMax !== scaleMin) {
      return null
    }
    return degenerateFiveBand(points, scaleMin, polarity)
  }

  const palette = fiveBandPalette(polarity)
  const scaleValues = collectScaleValues(scaleMin, scaleMax)
  const counts: Array<number> = Array.from({ length: FIVE_BAND_COUNT }, () => 0)
  let total = 0

  for (const point of points) {
    if (point.rawValue !== null && typeof point.rawValue === 'object') {
      continue
    }
    const raw = Number(point.rawValue)
    const count = Number(point.value) || 0
    if (!Number.isFinite(raw) || count <= 0) {
      continue
    }
    const index = fiveBandIndex(raw, scaleMin, scaleMax)
    if (index < 0) {
      continue
    }
    counts[index] += count
    total += count
  }

  if (total <= 0) {
    return null
  }

  let leftPct = 0
  return counts.map((count, index) => {
    const widthPct = (100 * count) / total
    const band: FiveBand = {
      index,
      scaleValues: scaleValues[index],
      count,
      total,
      percent: widthPct,
      leftPct,
      widthPct,
      label: formatScaleRange(scaleValues[index]),
      color: palette[index],
    }
    leftPct += widthPct
    return band
  })
}
